import React, { useState, useEffect, useContext, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../App';
import {
  User,
  BookOpen,
  Heart,
  MessageCircle,
  Calendar,
  Share2,
  Sparkles,
  Play,
  Edit3,
  Check,
  X,
  Camera,
  Loader2
} from 'lucide-react';
import { toast } from 'sonner';

const Profile = () => {
  const { username } = useParams();
  const { API, user, setUser } = useContext(AuthContext);
  const [profile, setProfile] = useState(null);
  const [comics, setComics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isEditingBio, setIsEditingBio] = useState(false);
  const [bioText, setBioText] = useState('');
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const fileInputRef = useRef(null);

  const isOwnProfile = user && (user.username === username || user.display_name === username);

  useEffect(() => {
    fetchProfile();
  }, [username]);

  const fetchProfile = async () => {
    try {
      setLoading(true);

      // Fetch comics by this author
      const comicsResponse = await axios.get(`${API}/streamlined-comics?limit=100`);
      const authorComics = comicsResponse.data.filter(
        comic => comic.author_name === username
      );

      if (authorComics.length > 0) {
        const authorInfo = {
          username: authorComics[0].author_name,
          display_name: authorComics[0].author_name,
          bio: isOwnProfile && user?.bio ? user.bio : "Comic creator and storyteller ✨",
          avatar: isOwnProfile ? user?.avatar : null,
          comics_count: authorComics.length,
          total_likes: authorComics.reduce((sum, comic) => sum + (comic.likes_count || 0), 0),
          total_comments: authorComics.reduce((sum, comic) => sum + (comic.comments_count || 0), 0),
          total_episodes: authorComics.reduce((sum, comic) => sum + (comic.episodes?.length || 0), 0)
        };

        setProfile(authorInfo);
        setComics(authorComics);
        setBioText(authorInfo.bio);
      } else {
        // Try legacy comics endpoint
        try {
          const legacyResponse = await axios.get(`${API}/comics?limit=50`);
          const legacyComics = legacyResponse.data.filter(comic => comic.author_name === username);
          if (legacyComics.length > 0) {
            setProfile({
              username: legacyComics[0].author_name,
              display_name: legacyComics[0].author_name,
              bio: isOwnProfile && user?.bio ? user.bio : "Comic creator and storyteller ✨",
              avatar: isOwnProfile ? user?.avatar : null,
              comics_count: legacyComics.length,
              total_likes: legacyComics.reduce((sum, comic) => sum + (comic.likes_count || 0), 0),
              total_comments: legacyComics.reduce((sum, comic) => sum + (comic.comments_count || 0), 0),
              total_episodes: 0
            });
            setComics(legacyComics);
          }
        } catch (e) {
          // If own profile but no comics, still show profile
          if (isOwnProfile) {
            setProfile({
              username: user.username,
              display_name: user.display_name || user.username,
              bio: user.bio || "Comic creator and storyteller ✨",
              avatar: user.avatar || null,
              comics_count: 0,
              total_likes: 0,
              total_comments: 0,
              total_episodes: 0
            });
            setBioText(user.bio || "Comic creator and storyteller ✨");
          }
        }
      }

    } catch (error) {
      console.error('Error fetching profile:', error);
      // If own profile, show even on error
      if (isOwnProfile) {
        setProfile({
          username: user.username,
          display_name: user.display_name || user.username,
          bio: user.bio || "Comic creator and storyteller ✨",
          avatar: user.avatar || null,
          comics_count: 0,
          total_likes: 0,
          total_comments: 0,
          total_episodes: 0
        });
        setBioText(user.bio || "Comic creator and storyteller ✨");
      } else {
        toast.error('Failed to load profile');
      }
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const handleShare = () => {
    const url = window.location.href;
    navigator.clipboard.writeText(url).then(() => {
      toast.success('Profile link copied to clipboard!');
    }).catch(() => {
      toast.error('Failed to copy link');
    });
  };

  const handleAvatarClick = () => {
    if (isOwnProfile && fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleAvatarUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file
    if (!file.type.startsWith('image/')) {
      toast.error('Please select an image file');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be under 5MB');
      return;
    }

    try {
      setUploadingAvatar(true);
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.put(`${API}/users/avatar`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          Authorization: `Bearer ${localStorage.getItem('token')}`
        }
      });

      if (response.data.success) {
        const newAvatarUrl = response.data.avatar;
        // Update local profile
        setProfile(prev => ({ ...prev, avatar: newAvatarUrl }));
        // Update global user context so Navbar updates immediately
        setUser(prev => ({ ...prev, avatar: newAvatarUrl }));
        toast.success('Profile picture updated!');
      }
    } catch (error) {
      console.error('Avatar upload error:', error);
      toast.error('Failed to upload profile picture');
    } finally {
      setUploadingAvatar(false);
      // Reset file input
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleSaveBio = async () => {
    try {
      const response = await axios.put(`${API}/users/bio`,
        { bio: bioText },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );

      if (response.data.success) {
        setProfile(prev => ({ ...prev, bio: bioText }));
        setUser(prev => ({ ...prev, bio: bioText }));
        setIsEditingBio(false);
        toast.success('Bio updated!');
      }
    } catch (error) {
      console.error('Bio update error:', error);
      toast.error('Failed to update bio');
    }
  };

  // Loading State
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-2 border-purple-500 border-t-transparent mx-auto mb-4"></div>
          <p className="text-gray-400 text-sm">Loading profile...</p>
        </div>
      </div>
    );
  }

  // Not found state
  if (!profile) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="text-center bg-slate-900/80 border border-purple-500/20 rounded-2xl p-12 max-w-md">
          <User className="w-20 h-20 mx-auto text-gray-600 mb-6" />
          <h3 className="text-2xl font-bold text-white mb-3">Creator Not Found</h3>
          <p className="text-gray-400 mb-6">This creator hasn't published any comics yet.</p>
          <Link to="/" className="inline-flex items-center px-6 py-3 rounded-xl bg-purple-600 hover:bg-purple-700 text-white font-semibold transition-colors">
            <Sparkles className="w-4 h-4 mr-2" />
            Explore Comics
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 relative overflow-hidden">
      {/* Hidden file input for avatar upload */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleAvatarUpload}
        className="hidden"
      />

      {/* Background Effects */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 right-[-10%] w-[500px] h-[500px] bg-purple-600/8 rounded-full blur-[150px]"></div>
        <div className="absolute bottom-[-20%] left-[-10%] w-[400px] h-[400px] bg-violet-500/8 rounded-full blur-[120px]"></div>
      </div>

      <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 pt-24">

        {/* Profile Header Card */}
        <div className="relative bg-slate-900/60 backdrop-blur-xl border border-purple-500/20 rounded-3xl p-8 mb-8 overflow-hidden">
          {/* Gradient accent bar */}
          <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-purple-600 via-violet-500 to-cyan-400"></div>

          <div className="flex flex-col md:flex-row items-center md:items-start gap-6">
            {/* Avatar */}
            <div className="relative flex-shrink-0 group">
              <div
                onClick={handleAvatarClick}
                className={`w-28 h-28 rounded-2xl overflow-hidden shadow-2xl shadow-purple-500/30 ${isOwnProfile ? 'cursor-pointer' : ''}`}
              >
                {(profile.avatar || (isOwnProfile && user?.avatar)) ? (
                  <img
                    src={profile.avatar || user?.avatar}
                    alt={profile.display_name}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="w-full h-full bg-gradient-to-br from-purple-600 to-cyan-500 flex items-center justify-center text-white text-4xl font-black">
                    {profile.display_name?.charAt(0)?.toUpperCase() || '?'}
                  </div>
                )}

                {/* Upload overlay (own profile only) */}
                {isOwnProfile && !uploadingAvatar && (
                  <div className="absolute inset-0 bg-black/60 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity rounded-2xl">
                    <Camera className="w-6 h-6 text-white" />
                  </div>
                )}

                {/* Loading overlay */}
                {uploadingAvatar && (
                  <div className="absolute inset-0 bg-black/70 flex items-center justify-center rounded-2xl">
                    <Loader2 className="w-6 h-6 text-purple-400 animate-spin" />
                  </div>
                )}
              </div>

              {isOwnProfile && (
                <div className="absolute -bottom-2 -right-2 w-7 h-7 bg-emerald-500 rounded-full border-3 border-slate-900 flex items-center justify-center">
                  <Check className="w-3.5 h-3.5 text-white" />
                </div>
              )}
            </div>

            {/* Info */}
            <div className="flex-1 text-center md:text-left">
              <div className="flex flex-col md:flex-row md:items-center gap-3 mb-3">
                <h1 className="text-3xl font-black text-white">{profile.display_name}</h1>
                {isOwnProfile && (
                  <span className="inline-flex items-center px-3 py-1 rounded-full bg-purple-500/20 border border-purple-500/30 text-purple-300 text-xs font-semibold">
                    Your Profile
                  </span>
                )}
              </div>
              <p className="text-gray-400 text-sm mb-4">@{profile.username}</p>

              {/* Bio */}
              <div className="mb-6">
                {isOwnProfile && isEditingBio ? (
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      value={bioText}
                      onChange={(e) => setBioText(e.target.value)}
                      className="flex-1 bg-slate-800 border border-purple-500/30 rounded-lg px-3 py-2 text-gray-200 text-sm focus:outline-none focus:border-purple-500"
                      maxLength={200}
                      autoFocus
                    />
                    <button onClick={handleSaveBio} className="p-2 rounded-lg bg-emerald-500/20 text-emerald-400 hover:bg-emerald-500/30 transition-colors">
                      <Check className="w-4 h-4" />
                    </button>
                    <button onClick={() => { setIsEditingBio(false); setBioText(profile.bio); }} className="p-2 rounded-lg bg-red-500/20 text-red-400 hover:bg-red-500/30 transition-colors">
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <p className="text-gray-300 text-sm">{profile.bio}</p>
                    {isOwnProfile && (
                      <button onClick={() => setIsEditingBio(true)} className="p-1.5 rounded-lg hover:bg-white/5 text-gray-500 hover:text-purple-400 transition-colors">
                        <Edit3 className="w-3.5 h-3.5" />
                      </button>
                    )}
                  </div>
                )}
              </div>

              {/* Stats */}
              <div className="flex flex-wrap justify-center md:justify-start gap-6">
                <div className="text-center">
                  <div className="text-2xl font-black text-white">{profile.comics_count}</div>
                  <div className="text-xs text-gray-500 uppercase tracking-wider font-semibold">Comics</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-black text-pink-400">{profile.total_likes}</div>
                  <div className="text-xs text-gray-500 uppercase tracking-wider font-semibold">Likes</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-black text-cyan-400">{profile.total_comments}</div>
                  <div className="text-xs text-gray-500 uppercase tracking-wider font-semibold">Comments</div>
                </div>
                {profile.total_episodes > 0 && (
                  <div className="text-center">
                    <div className="text-2xl font-black text-violet-400">{profile.total_episodes}</div>
                    <div className="text-xs text-gray-500 uppercase tracking-wider font-semibold">Episodes</div>
                  </div>
                )}
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-2 flex-shrink-0">
              <button
                onClick={handleShare}
                className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-slate-800 border border-gray-700 text-gray-300 hover:text-white hover:border-purple-500/50 transition-all text-sm font-medium"
              >
                <Share2 className="w-4 h-4" />
                Share
              </button>
            </div>
          </div>
        </div>

        {/* Comics Section Header */}
        <div className="flex items-center gap-3 mb-6">
          <div className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-gradient-to-r from-purple-600 to-violet-600 text-white shadow-lg shadow-purple-500/30 text-sm font-bold">
            <BookOpen className="w-4 h-4" />
            Comics ({comics.length})
          </div>
        </div>

        {/* Comics Grid */}
        {comics.length === 0 ? (
          <div className="text-center py-20 bg-slate-900/40 border border-gray-800 rounded-2xl">
            <BookOpen className="w-16 h-16 mx-auto text-gray-600 mb-4" />
            <h3 className="text-xl font-bold text-gray-400 mb-2">No comics yet</h3>
            <p className="text-gray-500">
              {isOwnProfile ? "You haven't published any comics yet. Start creating!" : "This creator hasn't published any comics."}
            </p>
            {isOwnProfile && (
              <Link to="/create-comic" className="inline-flex items-center mt-4 px-6 py-3 rounded-xl bg-purple-600 hover:bg-purple-700 text-white font-semibold transition-colors">
                <Sparkles className="w-4 h-4 mr-2" />
                Create Your First Comic
              </Link>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
            {comics.map((comic, index) => (
              <Link
                key={comic.id}
                to={`/streamlined-comic/${comic.id}`}
                className="group relative aspect-[2/3] rounded-xl overflow-hidden transition-all duration-300 hover:scale-105 hover:ring-1 hover:ring-purple-400/40 hover:shadow-xl hover:shadow-purple-500/20"
                style={{ animationDelay: `${index * 0.05}s` }}
              >
                {/* Cover Image */}
                {(comic.episodes?.[0]?.slides?.[0] || comic.images?.[0]) ? (
                  <img
                    src={comic.episodes?.[0]?.slides?.[0] || comic.images?.[0]}
                    alt={comic.title}
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                  />
                ) : (
                  <div className="absolute inset-0 w-full h-full bg-gradient-to-br from-purple-900 to-slate-800 flex items-center justify-center">
                    <Sparkles className="w-10 h-10 text-purple-500/40" />
                  </div>
                )}

                {/* Overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/20 to-transparent opacity-80 group-hover:opacity-60 transition-opacity"></div>

                {/* Category Badge */}
                <div className="absolute top-2 right-2 bg-purple-600/80 backdrop-blur-sm px-2 py-0.5 rounded-full text-[9px] font-bold text-white uppercase">
                  {comic.categories?.[0] || 'Comic'}
                </div>

                {/* NEW Badge — comic speech bubble style */}
                {comic.updated_at && (Date.now() - new Date(comic.updated_at).getTime()) < 7 * 24 * 60 * 60 * 1000 && (
                  <div className="absolute top-1 left-1 z-20" style={{ animation: 'badgeBounce 2s ease-in-out infinite' }}>
                    <div className="relative">
                      <div className="bg-yellow-400 text-black px-2.5 py-1 rounded-lg text-[10px] font-black uppercase tracking-wide shadow-lg"
                        style={{
                          border: '2px solid #000',
                          textShadow: '0 0 4px rgba(255,255,0,0.3)',
                          boxShadow: '0 0 12px rgba(250,204,21,0.6), 0 2px 8px rgba(0,0,0,0.4)'
                        }}>
                        ⚡ NEW!
                      </div>
                      {/* Speech bubble tail */}
                      <div className="absolute -bottom-1.5 left-3 w-0 h-0"
                        style={{
                          borderLeft: '5px solid transparent',
                          borderRight: '5px solid transparent',
                          borderTop: '6px solid #000'
                        }}></div>
                      <div className="absolute -bottom-1 left-3.5 w-0 h-0"
                        style={{
                          borderLeft: '4px solid transparent',
                          borderRight: '4px solid transparent',
                          borderTop: '5px solid #facc15'
                        }}></div>
                    </div>
                  </div>
                )}

                {/* Episode Count */}
                {comic.episodes && comic.episodes.length > 1 && (
                  <div className="absolute top-8 right-2 bg-cyan-500/80 backdrop-blur-sm px-2 py-0.5 rounded-full text-[9px] font-bold text-white">
                    {comic.episodes.length} Eps
                  </div>
                )}

                {/* Bottom Info */}
                <div className="absolute bottom-0 left-0 right-0 p-3">
                  <p className="text-sm font-bold text-white line-clamp-2 mb-1 drop-shadow-lg">
                    {comic.title}
                  </p>
                  <div className="flex items-center gap-3 mt-1 text-[10px] text-gray-400">
                    <span className="flex items-center gap-1">
                      <Heart className="w-3 h-3 text-pink-400" />
                      {comic.likes_count || 0}
                    </span>
                    <span className="flex items-center gap-1">
                      <MessageCircle className="w-3 h-3 text-cyan-400" />
                      {comic.comments_count || 0}
                    </span>
                  </div>
                </div>

                {/* Hover Play Icon */}
                <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <div className="bg-purple-600/90 rounded-full p-3 shadow-xl shadow-purple-500/30 transform scale-75 group-hover:scale-100 transition-transform">
                    <Play className="w-5 h-5 text-white fill-current" />
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Profile;